/**
 * Local mirrors of the settings/projects API's response shapes.
 *
 * Kept in this feature folder rather than `@rundown/types` per this
 * milestone's file-ownership split — the lead folds the ones worth sharing
 * into the package once the three modules land in app.module.ts. See the
 * matching `types.ts` files under apps/api/src/modules/{settings,projects}
 * for the server side of each of these.
 */

import type { Role } from '@rundown/policy';

export interface WorkspaceSettings {
  readonly id: string;
  readonly name: string;
  readonly timezone: string;
  /** What the client calls the delivery side. Shown wherever the product names it. */
  readonly vendorName: string;
  readonly monthlyCapacityHours: number;
  readonly hotfixReservePct: number;
  readonly includedCreditUnits: number;
  /**
   * Where Small stops and Medium starts, in working days.
   *
   * Flat here because this screen edits four independent numbers; the same four
   * arrive nested as `Workspace.sizeThresholds` on the profile, which is what
   * every screen that draws a size label reads.
   */
  readonly sizeHoursPerDay: number;
  readonly sizeSmallMaxDays: number;
  readonly sizeMediumMaxDays: number;
  readonly sizeLargeMaxDays: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface WorkspaceSettingsPatch {
  readonly name?: string;
  readonly timezone?: string;
  readonly vendorName?: string;
  readonly monthlyCapacityHours?: number;
  readonly hotfixReservePct?: number;
  readonly includedCreditUnits?: number;
  readonly sizeHoursPerDay?: number;
  readonly sizeSmallMaxDays?: number;
  readonly sizeMediumMaxDays?: number;
  readonly sizeLargeMaxDays?: number;
}

/** Matches SECRET_KEYS in apps/api/src/modules/settings/settings.dto.ts. */
/** Mirrors the API's SECRET_KEYS — see settings.dto.ts for why it is this short. */
export const SECRET_KEYS = ['repo.github.credential'] as const;

export type SecretKey = (typeof SECRET_KEYS)[number];
export type SecretState = 'configured' | 'not configured';

export interface SecretStatus {
  readonly key: SecretKey;
  readonly status: SecretState;
  readonly updatedAt: string | null;
}

export type Grade = 'junior' | 'mid_senior' | 'senior';

export interface Membership {
  readonly id: string;
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly role: Role;
  /**
   * ── Delivery attributes, on the person ────────────────────────────────
   * There is no separate Engineer any more — see the header on `users` in
   * apps/api/src/db/schema/tenancy.ts. `weeklyHours` is what this person works
   * in total; `allocatedHours` is the share committed to THIS workspace.
   *
   * `grade` and `billingFactorBp` are OPTIONAL KEYS rather than nullable ones,
   * and the difference carries meaning: they are absent from the payload
   * entirely for a caller without `billing.read` (the server's field
   * projection), and present-but-null for someone who simply has no grade.
   * `hasBilling()` below is how the UI tells those two apart.
   */
  readonly weeklyHours: number;
  readonly allocatedHours: number;
  readonly grade?: Grade | null;
  readonly billingFactorBp?: number | null;
  readonly figmaHandle: string | null;
  /** Their seat in this workspace — not the same question as `accountSuspended`. */
  readonly active: boolean;
  /** Locked out of Rundown entirely. Comes free with the list; see MembershipDto. */
  readonly accountSuspended: boolean;
  readonly createdAt: string;
}

/**
 * True when the caller holds `billing.read` and the row therefore carries what
 * this person costs.
 *
 * Tests for the KEY, not for a value: the server omits `grade` entirely from a
 * Developer's payload rather than nulling it, so `membership.grade === null`
 * would read "no grade set" for a person who has one you simply may not see.
 */
export function hasBilling(
  membership: Membership,
): membership is Membership & { grade: Grade | null; billingFactorBp: number | null } {
  return 'grade' in membership;
}

/**
 * How a person signs in — deliberately a separate row from their membership.
 *
 * A membership answers "may they use this workspace"; an account answers "may
 * they sign in to Rundown at all". Suspending an account reaches every
 * workspace that person belongs to, where deactivating a membership reaches
 * one, and the screen has to keep that difference visible. Mirrors AccountDto
 * in apps/api/src/modules/settings/types.ts.
 */
export type AccountSignIn = 'none' | 'google' | 'password';

export interface Account {
  readonly membershipId: string;
  readonly userId: string;
  readonly email: string;
  readonly signIn: AccountSignIn;
  readonly emailVerified: boolean;
  readonly suspended: boolean;
  readonly mfaEnabled: boolean;
  /** `app.users.active` — the switch that actually stops every request. */
  readonly active: boolean;
}

export interface SetAccountPasswordInput {
  readonly password: string;
  /** Defaults to true server-side; sent explicitly so the screen owns the choice. */
  readonly mustChange: boolean;
}

export interface InviteMemberInput {
  readonly email: string;
  readonly name: string;
  readonly role: Role;
  readonly figmaHandle?: string | null;
  /** Only meaningful for a Noorix role; the dialog omits them for client roles. */
  readonly grade?: Grade | null;
  readonly billingFactorBp?: number | null;
  readonly weeklyHours?: number;
  readonly allocatedHours?: number;
}

export interface UpdateMembershipInput {
  readonly role?: Role;
  readonly active?: boolean;
  readonly figmaHandle?: string | null;
  readonly grade?: Grade | null;
  readonly billingFactorBp?: number | null;
  readonly weeklyHours?: number;
  readonly allocatedHours?: number;
}

export interface Project {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}
